import { BorderRadiusSmall, ColorNordicGrey1, ColorNordicGrey4, Spacing4 } from 'autogen/design-tokens/tokens';
import type { ProductCertificationHighlight } from 'autogen/swagger/all';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import { Body } from 'components/generic/typography';
import { ProductAccordionContext } from 'components/product/accordion/hooks/context';
import { type HTMLAttributes, useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { format } from 'modules/helpers/strings';
import { Certified } from 'autogen/translation-keys/trans-website-imagealttext';
import PageAction from 'components/shared/Routing/PageAction';

interface ICertificationHighlights extends HTMLAttributes<HTMLUListElement> {
    highlights: ProductCertificationHighlight[];
}

const BoxStyling = css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
    width: 32px;
    background-color: ${ColorNordicGrey4};
    border-radius: ${BorderRadiusSmall};
    padding: ${Spacing4};
`;

const Wrapper = styled.ul`
    display: flex;
    flex-wrap: wrap;
    list-style: none;
    padding: 0;
    margin-bottom: 0;
    gap: ${Spacing4};
`;

const Box = styled.div`
    ${BoxStyling}
`;

const BoxButton = styled.button`
    ${BoxStyling}
    border: 0;
`;

const ImagePlaceholder = styled.span`
    width: 100%;
    height: 100%;
    border-radius: 100%;
    background-color: ${ColorNordicGrey1};
`;

const StyledPicture = styled(PictureDeprecated)`
    img {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }
`;

function getHighlightsWithImages(
    highlights: ProductCertificationHighlight[],
    maxCertificationsToShow: number
): ProductCertificationHighlight[] {
    return highlights
        .reverse()
        .sort((item) => (item.image ? -1 : 1))
        .splice(0, maxCertificationsToShow);
}

export default function CertificationHighlights({ highlights, ...restProps }: ICertificationHighlights): JSX.Element {
    const certificationAmount = highlights.length;
    const maxCertificationsToShow = 2;
    const additionalCertificates = certificationAmount - maxCertificationsToShow;
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const firstHighlightsWithImages = useMemo(
        () => getHighlightsWithImages([...highlights], maxCertificationsToShow),
        [highlights, maxCertificationsToShow]
    );
    const context = useContext(ProductAccordionContext);
    const { t } = useTranslation();

    return (
        <Wrapper {...restProps}>
            {!!highlights.length &&
                firstHighlightsWithImages.map((item): JSX.Element => {
                    const { name, image, pageAction } = item;

                    const box = (
                        <Box>{image ? <StyledPicture {...image} lazyLoad /> : <ImagePlaceholder key={name} />}</Box>
                    );

                    return pageAction ? (
                        <li key={name}>
                            <PageAction action={pageAction} aria-label={format(t(Certified))(name)}>
                                {box}
                            </PageAction>
                        </li>
                    ) : (
                        <li key={name}>{box}</li>
                    );
                })}

            {additionalCertificates > 0 && (
                <li>
                    <BoxButton onClick={() => context('Certifications')}>
                        <Body as="span" size="M" strong>
                            &#43;{additionalCertificates}
                        </Body>
                    </BoxButton>
                </li>
            )}
        </Wrapper>
    );
}
